import React, { useState, useEffect } from "react";
import { salesClientsApi } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import CountrySelector from "./CountrySelector";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { toast } from "sonner";
import { Check, ChevronsUpDown, Plus, Building2, User } from "lucide-react";
import { cn } from "../lib/utils";

export default function ClientSelector({ value, onChange, clients = [], onClientCreated, compact = false }) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [newClient, setNewClient] = useState({
    name: "",
    country: "",
    contact_person: "",
    email: "",
    phone: "",
  });
  const [creating, setCreating] = useState(false);

  const selectedClient = clients.find(c => c.id === value);

  const filteredClients = clients.filter(c =>
    c.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleCreateClient = async () => {
    if (!newClient.name.trim()) {
      toast.error("Client name is required");
      return;
    }

    try {
      setCreating(true);
      const res = await salesClientsApi.create(newClient);
      toast.success("Client created");
      setCreateOpen(false);
      setNewClient({ name: "", country: "", contact_person: "", email: "", phone: "" });
      
      // Select the newly created client
      onChange(res.data.id, res.data.name);
      
      // Notify parent to refresh clients list
      if (onClientCreated) {
        onClientCreated(res.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create client");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex gap-2 min-w-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "flex-1 min-w-0 justify-between bg-zinc-800 border-zinc-700 text-zinc-50 hover:bg-zinc-700",
              compact && "h-8 text-sm"
            )}
            data-testid="client-selector-trigger"
          >
            {selectedClient ? (
              <span className="flex items-center gap-2 min-w-0">
                <Building2 className={cn("shrink-0 text-sky-400", compact ? "h-3 w-3" : "h-4 w-4")} />
                <span className="truncate">{selectedClient.name}</span>
              </span>
            ) : (
              <span className="text-zinc-400 truncate">Select client...</span>
            )}
            <ChevronsUpDown className={cn("ml-2 shrink-0 opacity-50", compact ? "h-3 w-3" : "h-4 w-4")} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0 bg-zinc-900 border-zinc-800" align="start">
          <Command className="bg-zinc-900">
            <CommandInput 
              placeholder="Search clients..." 
              value={searchValue}
              onValueChange={setSearchValue}
              className="bg-zinc-900 text-zinc-50"
            />
            <CommandList>
              <CommandEmpty className="py-4 text-center text-zinc-500">
                No clients found.
                <Button
                  variant="link"
                  className="text-sky-400 p-0 h-auto ml-1"
                  onClick={() => {
                    setOpen(false);
                    setNewClient({ ...newClient, name: searchValue });
                    setCreateOpen(true);
                  }}
                >
                  Create "{searchValue}"?
                </Button>
              </CommandEmpty>
              <CommandGroup>
                {filteredClients.map((client) => (
                  <CommandItem
                    key={client.id}
                    value={client.name}
                    onSelect={() => {
                      onChange(client.id, client.name);
                      setOpen(false);
                    }}
                    className="text-zinc-50 hover:bg-zinc-800 cursor-pointer"
                    data-testid={`client-option-${client.id}`}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === client.id ? "opacity-100 text-sky-400" : "opacity-0"
                      )}
                    />
                    <div className="flex-1">
                      <p>{client.name}</p>
                      {client.country && (
                        <p className="text-xs text-zinc-500">{client.country}</p>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogTrigger asChild>
          <Button 
            variant="outline" 
            size="icon"
            className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700"
            data-testid="create-client-quick-btn"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <User className="h-5 w-5 text-sky-400" />
              Quick Create Client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-300">Client Name *</Label>
              <Input
                value={newClient.name}
                onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
                placeholder="Client or company name"
                data-testid="quick-client-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Country</Label>
                <CountrySelector
                  value={newClient.country}
                  onChange={(country) => setNewClient({ ...newClient, country })}
                  placeholder="Select country..."
                />
              </div>
              <div>
                <Label className="text-zinc-300">Contact Person</Label>
                <Input
                  value={newClient.contact_person}
                  onChange={(e) => setNewClient({ ...newClient, contact_person: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder="Contact name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Email</Label>
                <Input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder="email@company.com"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Phone</Label>
                <Input
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                  placeholder="+1 234 567 890"
                />
              </div>
            </div>
            <p className="text-xs text-zinc-500">
              For full client details (billing, bank info), visit the Clients page after creation.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button 
                onClick={handleCreateClient} 
                disabled={creating}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="quick-client-save"
              >
                {creating ? "Creating..." : "Create Client"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
